<!-- resources/js/components/EmailReports.vue -->
<template>
  <div class="email-reports">
    <div class="reports-container">
      <!-- Tabela de Envios -->
      <div class="reports-table-section">
        <!-- Tarja informativa -->
        <div class="info-banner" :class="isInvalidadosMode ? 'banner-api' : 'banner-brevo'">
          <i :class="isInvalidadosMode ? 'fa-solid fa-microchip' : 'fa-solid fa-server'"></i>
          <span v-if="!isInvalidadosMode">
            Os e-mails consultados nas pesquisas por <strong>CPD, e-mail, razão social e CPF/CNPJ</strong>
            passam por validação da <strong>Brevo</strong>, considerando a tentativa de entrega realizada.
          </span>
          <span v-else>
            Os contatos listados tiveram seus e-mails validados pela <strong>API de Validação</strong>,
            confirmando que o endereço não existe no servidor de destino.
          </span>
        </div>
        <div class="table-header">
          <h2><i class="fa-solid fa-table-list"></i> Detalhamento de Envios</h2>
          <div class="header-actions">
            <div class="table-info">
              <span v-if="loading">Carregando...</span>
              <span v-else-if="!activeSearch">0 carregados</span>
              <span v-else-if="isInvalidadosMode">
                <template v-if="localFilter">{{ formatNumber(filteredInvalidados.length) }} exibidos de </template>
                {{ formatNumber(invalidados.length) }} carregados
                <template v-if="totalInvalidadosRecords > invalidados.length"> de {{
                  formatNumber(totalInvalidadosRecords) }} no total</template>
              </span>
              <span v-else>
                <template v-if="localFilter">{{ formatNumber(filteredReports.length) }} exibidos de </template>
                {{ formatNumber(reports.length) }} carregados
                <template v-if="totalServerRecords > reports.length"> de {{ formatNumber(totalServerRecords) }} no
                  total</template>
              </span>
            </div>
            <!-- Botão de Exportar -->
            <button v-if="(isInvalidadosMode ? invalidados.length : reports.length) > 0" @click="exportToExcel"
              class="btn-export" :disabled="exporting">
              <i class="fa-solid" :class="exporting ? 'fa-spinner fa-spin' : 'fa-file-excel'"></i>
              {{ exporting ? 'Exportando...' : `Exportar Excel (${formatNumber(isInvalidadosMode ?
                filteredInvalidados.length :
                filteredReports.length)})` }}
            </button>
          </div>
        </div>

        <!-- Barra de Pesquisa Avançada -->
        <div class="search-bar">
          <div class="search-controls">
            <!-- Seletor de Tipo de Pesquisa -->
            <div class="search-type-selector">
              <label><i class="fa-solid fa-filter"></i> Pesquisar por</label>
              <select v-model="searchType" @change="onSearchTypeChange" class="search-type-select">
                <option value="cpd">CPD (exato)</option>
                <option value="email">Email (contém)</option>
                <option value="nome">Razão Social (contém)</option>
                <option value="cpf_cnpj">CPF/CNPJ (exato)</option>
                <option value="contatos_invalidados">Contatos Invalidados</option>
              </select>
            </div>

            <!-- Campo de Pesquisa — oculto quando "Contatos Invalidados" -->
            <div v-if="searchType !== 'contatos_invalidados'" class="search-input-wrapper">
              <i class="fa-solid fa-magnifying-glass search-icon"></i>
              <input type="text" v-model="searchQuery" @input="handleSearchInput" @keyup.enter="performSearch"
                class="search-input" :placeholder="getSearchPlaceholder()" />
              <button v-if="searchQuery" @click="clearSearch" class="clear-search-btn" title="Limpar pesquisa">
                <i class="fa-solid fa-xmark"></i>
              </button>
            </div>

            <!-- Info box quando "Contatos Invalidados" está selecionado -->
            <div v-else class="invalidados-info-box">
              <i class="fa-solid fa-circle-info"></i>
              <span>Lista todos os contatos validados pela <strong>API de validação</strong> nos campos principais ou
                CC</span>
            </div>

            <!-- Botão de Buscar -->
            <button @click="performSearch(false)" class="btn btn-search"
              :disabled="loading || (searchType !== 'contatos_invalidados' && !searchQuery.trim())">
              <i class="fa-solid" :class="loading ? 'fa-spinner fa-spin' : 'fa-magnifying-glass'"></i>
              {{ loading ? 'Buscando...' : (searchType === 'contatos_invalidados' ? 'Listar' : 'Buscar') }}
            </button>
          </div>

          <!-- Info da pesquisa ativa -->
          <div v-if="activeSearch" class="active-search-info">
            <span class="search-badge">
              <i class="fa-solid fa-filter"></i>
              Pesquisa ativa: <strong>{{ getSearchTypeLabel() }}</strong>
              <span v-if="activeSearchQuery"> = "{{ activeSearchQuery }}"</span>
            </span>
            <button @click="clearAllFilters" class="btn-clear-filters">
              <i class="fa-solid fa-xmark"></i> Limpar filtros
            </button>
          </div>
        </div>

        <!-- ═══════════════════════════════════════════════════════════
             FILTRO LOCAL — aparece após qualquer pesquisa ativa
        ════════════════════════════════════════════════════════════ -->
        <div v-if="activeSearch" class="local-filter-bar">
          <div class="local-filter-label">
            <i class="fa-solid fa-table-list"></i>
            Filtrar resultados:
          </div>
          <div class="local-filter-wrapper">
            <i class="fa-solid fa-magnifying-glass local-filter-icon"></i>
            <input type="text" v-model="localFilter" class="local-filter-input"
              placeholder="Digite para filtrar: email, nome, CPD, CNPJ..." />
            <button v-if="localFilter" @click="localFilter = ''" class="clear-local-btn" title="Limpar filtro">
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>
          <span class="local-filter-count" :class="localFilter && currentFilteredCount === 0 ? 'count-empty' : ''">
            <i class="fa-solid"
              :class="localFilter ? (currentFilteredCount > 0 ? 'fa-circle-check' : 'fa-circle-xmark') : 'fa-list-ul'"></i>
            <template v-if="localFilter">{{ formatNumber(currentFilteredCount) }} resultado(s)</template>
            <template v-else>{{ formatNumber(isInvalidadosMode ? invalidados.length : reports.length) }}
              registro(s)</template>
          </span>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Carregando envios...</p>
        </div>

        <!-- Tabela de envios normais -->
        <div v-else-if="!isInvalidadosMode && filteredReports.length > 0" class="table-responsive">
          <table class="reports-table">
            <thead>
              <tr>
                <th @click="sortBy('cpd')" class="sortable-th">
                  CPD <span class="sort-icons"><i :class="getSortIcon('cpd')"></i></span>
                </th>
                <th @click="sortBy('cpf_cnpj')" class="sortable-th">
                  C.N.P.J. <span class="sort-icons"><i :class="getSortIcon('cpf_cnpj')"></i></span>
                </th>
                <th @click="sortBy('nome')" class="sortable-th">
                  Razão Social <span class="sort-icons"><i :class="getSortIcon('nome')"></i></span>
                </th>
                <th @click="sortBy('campanha')" class="sortable-th">
                  Campanha <span class="sort-icons"><i :class="getSortIcon('campanha')"></i></span>
                </th>
                <th @click="sortBy('template')" class="sortable-th">
                  Template <span class="sort-icons"><i :class="getSortIcon('template')"></i></span>
                </th>
                <th @click="sortBy('lote')" class="sortable-th">
                  Lote <span class="sort-icons"><i :class="getSortIcon('lote')"></i></span>
                </th>
                <th @click="sortBy('email')" class="sortable-th">
                  E-mail <span class="sort-icons"><i :class="getSortIcon('email')"></i></span>
                </th>
                <th @click="sortBy('status')" class="sortable-th">
                  Status <span class="sort-icons"><i :class="getSortIcon('status')"></i></span>
                </th>
                <th @click="sortBy('enviado_em')" class="sortable-th">
                  Enviado em <span class="sort-icons"><i :class="getSortIcon('enviado_em')"></i></span>
                </th>
                <th @click="sortBy('aberto')" class="sortable-th">
                  Aberto <span class="sort-icons"><i :class="getSortIcon('aberto')"></i></span>
                </th>
                <th @click="sortBy('aberto_em')" class="sortable-th">
                  Data Abertura <span class="sort-icons"><i :class="getSortIcon('aberto_em')"></i></span>
                </th>
                <th @click="sortBy('total_cliques')" class="sortable-th">
                  Cliques <span class="sort-icons"><i :class="getSortIcon('total_cliques')"></i></span>
                </th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="report in paginatedReports" :key="report.id">
                <td>{{ getContactCpd(report) }}</td>
                <td>{{ formatCpfCnpj(getContactCpfCnpj(report)) }}</td>
                <td>{{ getContactName(report) }}</td>
                <td class="campaign-name">{{ getCampaignName(report) }}</td>
                <td>{{ report.campanha && report.campanha.lista ? report.campanha.lista.descricao || '-' : '-' }}</td>
                <td style="font-family: monospace; font-size: 12px; white-space: nowrap;">
                  {{ report.campanha && report.campanha.lista ? formatLote(report.campanha.lista.arquivo_importado) :
                    '-' }}
                </td>
                <td>
                  <div class="email-cell">
                    <i class="fa-solid fa-envelope"></i>
                    <span v-html="highlight(report.email)"></span>
                  </div>
                </td>
                <td>
                  <span class="status-badge" :class="getStatusClass(report.status)">
                    <i :class="getStatusIcon(report.status)"></i>
                    {{ getStatusLabel(report.status) }}
                  </span>
                </td>
                <td>{{ formatDateTime(report.enviado_em) }}</td>
                <td class="text-center">
                  <span v-if="report.aberto" class="badge-opened">
                    <i class="fa-solid fa-check"></i> Sim
                  </span>
                  <span v-else class="badge-not-opened">
                    <i class="fa-solid fa-xmark"></i> Não
                  </span>
                </td>
                <td>{{ formatDateTime(report.aberto_em) }}</td>
                <td class="text-center">
                  <span v-if="report.total_cliques > 0" class="badge-clicks">
                    <i class="fa-solid fa-mouse-pointer"></i> {{ report.total_cliques }}
                  </span>
                  <span v-else class="badge-no-clicks">0</span>
                </td>
                <td class="actions-cell">
                  <button @click="viewDetails(report)" class="btn-action btn-view" title="Ver detalhes">
                    <i class="fa-solid fa-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Tabela de Contatos Invalidados -->
        <div v-else-if="isInvalidadosMode && filteredInvalidados.length > 0" class="table-responsive">
          <table class="reports-table">
            <thead>
              <tr>
                <th @click="sortBy('cpd')" class="sortable-th">
                  CPD <span class="sort-icons"><i :class="getSortIcon('cpd')"></i></span>
                </th>
                <th @click="sortBy('cpf_cnpj')" class="sortable-th">
                  C.N.P.J. <span class="sort-icons"><i :class="getSortIcon('cpf_cnpj')"></i></span>
                </th>
                <th @click="sortBy('nome')" class="sortable-th">
                  Razão Social <span class="sort-icons"><i :class="getSortIcon('nome')"></i></span>
                </th>
                <th @click="sortBy('email_invalido')" class="sortable-th">
                  E-mail Inválido <span class="sort-icons"><i :class="getSortIcon('email_invalido')"></i></span>
                </th>
                <th @click="sortBy('tipo')" class="sortable-th">
                  Tipo <span class="sort-icons"><i :class="getSortIcon('tipo')"></i></span>
                </th>
                <th>Status</th>
                <th @click="sortBy('validado_em')" class="sortable-th">
                  Validado em <span class="sort-icons"><i :class="getSortIcon('validado_em')"></i></span>
                </th>
                <th @click="sortBy('lista_template')" class="sortable-th">
                  Template <span class="sort-icons"><i :class="getSortIcon('lista_template')"></i></span>
                </th>
                <th @click="sortBy('lista_arquivo')" class="sortable-th">
                  Lote <span class="sort-icons"><i :class="getSortIcon('lista_arquivo')"></i></span>
                </th>
                <th>Motivo</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in paginatedInvalidados" :key="index">
                <td>{{ row.cpd || '-' }}</td>
                <td>{{ formatCpfCnpj(row.cpf_cnpj) }}</td>
                <td v-html="highlight(row.nome || '-')"></td>
                <td>
                  <div class="email-cell" style="color:#ef4444">
                    <i class="fa-solid fa-envelope-circle-check"></i>
                    <span v-html="highlight(row.email_invalido)"></span>
                  </div>
                </td>
                <td>
                  <span class="status-badge" :class="row.tipo === 'principal' ? 'status-sent' : 'status-delivered'">
                    <i :class="row.tipo === 'principal' ? 'fa-solid fa-star' : 'fa-solid fa-copy'"></i>
                    {{ row.tipo === 'principal' ? 'Principal' : 'CC' }}
                  </span>
                </td>
                <td>
                  <span class="status-badge status-bounced">
                    <i class="fa-solid fa-circle-xmark"></i>
                    Inválido
                  </span>
                </td>
                <td>{{ formatDateTime(row.validado_em) }}</td>
                <td>{{ row.lista_template || '-' }}</td>
                <td style="font-family: monospace; font-size: 12px; white-space: nowrap;">
                  {{ formatLote(row.lista_arquivo) }}
                </td>
                <td style="font-size:12px;color:#6b7280;max-width:180px">{{ row.motivo_curto }}</td>
                <td class="actions-cell">
                  <button @click="viewInvalidadoDetails(row)" class="btn-action btn-view" title="Ver detalhes">
                    <i class="fa-solid fa-eye"></i>
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Empty State -->
        <div v-if="!loading && !activeSearch" class="empty-state">
          <i class="fa-solid fa-inbox empty-icon"></i>
          <h3>Nenhum envio encontrado</h3>
          <p>Utilize a pesquisa acima para buscar envios</p>
        </div>

        <div v-if="!loading && activeSearch && currentTableEmpty" class="empty-state">
          <i class="fa-solid fa-inbox empty-icon"></i>
          <h3>Nenhum {{ isInvalidadosMode ? 'contato invalidado' : 'envio' }} encontrado</h3>
          <p>Tente ajustar sua pesquisa</p>
        </div>

        <!-- Paginação LOCAL — envios normais -->
        <div v-if="!isInvalidadosMode && filteredReports.length > itemsPerPage && !loading"
          class="pagination-container">
          <div class="pagination-info">
            Mostrando {{ formatNumber(startIndex + 1) }} - {{ formatNumber(endIndex) }} de {{
              formatNumber(filteredReports.length) }} envios
            <span v-if="hasMoreData" class="text-muted"> ({{ formatNumber(totalServerRecords) }} no total)</span>
          </div>
          <div class="pagination-controls">
            <button @click="goToPage(1)" class="btn btn-secondary" :disabled="currentPage === 1">
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button @click="previousPage" class="btn btn-secondary" :disabled="currentPage === 1">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <div class="pagination-pages">
              <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" class="btn"
                :class="currentPage === page ? 'btn-primary' : 'btn-secondary'">
                {{ page }}
              </button>
            </div>
            <button @click="nextPage" class="btn btn-secondary" :disabled="currentPage === totalPages">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button @click="goToPage(totalPages)" class="btn btn-secondary" :disabled="currentPage === totalPages">
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>

        <!-- Paginação LOCAL — invalidados -->
        <div v-if="isInvalidadosMode && filteredInvalidados.length > itemsPerPage && !loading"
          class="pagination-container">
          <div class="pagination-info">
            Mostrando {{ formatNumber(startIndexInv + 1) }} - {{ formatNumber(endIndexInv) }} de {{
              formatNumber(filteredInvalidados.length) }} contatos
            <span v-if="hasMoreInvalidadosData" class="text-muted"> ({{ formatNumber(totalInvalidadosRecords) }} no
              total)</span>
          </div>
          <div class="pagination-controls">
            <button @click="goToPageInv(1)" class="btn btn-secondary" :disabled="currentPageInv === 1">
              <i class="fa-solid fa-angles-left"></i>
            </button>
            <button @click="previousPageInv" class="btn btn-secondary" :disabled="currentPageInv === 1">
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <div class="pagination-pages">
              <button v-for="page in visiblePagesInv" :key="page" @click="goToPageInv(page)" class="btn"
                :class="currentPageInv === page ? 'btn-primary' : 'btn-secondary'">
                {{ page }}
              </button>
            </div>
            <button @click="nextPageInv" class="btn btn-secondary" :disabled="currentPageInv === totalPagesInv">
              <i class="fa-solid fa-chevron-right"></i>
            </button>
            <button @click="goToPageInv(totalPagesInv)" class="btn btn-secondary"
              :disabled="currentPageInv === totalPagesInv">
              <i class="fa-solid fa-angles-right"></i>
            </button>
          </div>
        </div>

        <!-- Botão Carregar Mais — envios normais -->
        <div v-if="!isInvalidadosMode && hasMoreData && !loading" class="load-more-container">
          <div class="load-more-content">
            <div class="load-more-stats">
              <span class="stats-loaded">{{ formatNumber(reports.length) }}</span>
              <span class="stats-separator">/</span>
              <span class="stats-total">{{ formatNumber(totalServerRecords) }}</span>
              <span class="stats-label">registros</span>
            </div>
            <div class="progress-mini">
              <div class="progress-mini-fill" :style="{ width: loadedPercentage + '%' }"></div>
            </div>
            <button @click="loadMoreData" class="btn-load-compact" :disabled="loadingMore">
              <i class="fa-solid" :class="loadingMore ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
              <span v-if="!loadingMore">Carregar mais 10.000</span>
              <span v-else>Carregando...</span>
            </button>
          </div>
        </div>

        <!-- Botão Carregar Mais — invalidados -->
        <div v-if="isInvalidadosMode && hasMoreInvalidadosData && !loading" class="load-more-container">
          <div class="load-more-content">
            <div class="load-more-stats">
              <span class="stats-loaded">{{ formatNumber(invalidados.length) }}</span>
              <span class="stats-separator">/</span>
              <span class="stats-total">{{ formatNumber(totalInvalidadosRecords) }}</span>
              <span class="stats-label">registros</span>
            </div>
            <div class="progress-mini">
              <div class="progress-mini-fill" :style="{ width: loadedInvalidadosPercentage + '%' }"></div>
            </div>
            <button @click="loadMoreInvalidados" class="btn-load-compact" :disabled="loadingMore">
              <i class="fa-solid" :class="loadingMore ? 'fa-spinner fa-spin' : 'fa-plus'"></i>
              <span v-if="!loadingMore">Carregar mais 10.000</span>
              <span v-else>Carregando...</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes — envio normal (inalterado) -->
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal-content-2 modal-large" @click.stop>
        <div class="modal-header">
          <div class="modal-header-content">
            <div class="modal-icon">
              <i class="fa-solid fa-file-lines"></i>
            </div>
            <div class="modal-title-wrapper">
              <h3>Detalhes do Envio</h3>
              <p class="modal-subtitle">Informações completas do email enviado</p>
            </div>
          </div>
          <button @click="closeDetailsModal" class="modal-close-btn" type="button">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body">
          <div v-if="selectedReport">

            <div class="info-section">
              <div class="section-header">
                <i class="fa-solid fa-user-circle"></i>
                <h4>Destinatário</h4>
              </div>
              <div class="detail-grid-enhanced">
                <div class="detail-card">
                  <div class="detail-icon email-icon"><i class="fa-solid fa-envelope"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">Email</span>
                    <span class="detail-value">{{ selectedReport.email }}</span>
                  </div>
                </div>
                <div class="detail-card">
                  <div class="detail-icon user-icon"><i class="fa-solid fa-user"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">Nome</span>
                    <span class="detail-value">{{ getContactName(selectedReport) }}</span>
                  </div>
                </div>
                <div class="detail-card">
                  <div class="detail-icon cpd-icon"><i class="fa-solid fa-id-card"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">CPD</span>
                    <span class="detail-value">{{ getContactCpd(selectedReport) }}</span>
                  </div>
                </div>
                <div class="detail-card">
                  <div class="detail-icon cpf-icon"><i class="fa-solid fa-id-badge"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">CPF/CNPJ</span>
                    <span class="detail-value">{{ formatCpfCnpj(getContactCpfCnpj(selectedReport)) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-section">
              <div class="section-header">
                <i class="fa-solid fa-paper-plane"></i>
                <h4>Campanha</h4>
              </div>
              <div class="detail-grid-enhanced">
                <div class="detail-card full-width">
                  <div class="detail-icon campaign-icon"><i class="fa-solid fa-bullhorn"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">Nome da Campanha</span>
                    <span class="detail-value campaign-name">{{ getCampaignName(selectedReport) }}</span>
                  </div>
                </div>
                <div class="detail-card">
                  <div class="detail-icon message-icon"><i class="fa-solid fa-hashtag"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">Message ID</span>
                    <span class="detail-value small-text">{{ selectedReport.brevo_message_id || '-' }}</span>
                  </div>
                </div>
                <div class="detail-card">
                  <div class="detail-icon status-icon"><i :class="getStatusIcon(selectedReport.status)"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">Status</span>
                    <span class="status-badge-enhanced" :class="getStatusClass(selectedReport.status)">
                      <i :class="getStatusIcon(selectedReport.status)"></i>
                      {{ getStatusLabel(selectedReport.status) }}
                    </span>
                  </div>
                </div>
                <div class="detail-card">
                  <div class="detail-icon calendar-icon"><i class="fa-solid fa-calendar"></i></div>
                  <div class="detail-content">
                    <span class="detail-label">Data de Envio</span>
                    <span class="detail-value">{{ formatDateTime(selectedReport.enviado_em) }}</span>
                  </div>
                </div>
                <!-- Template e Lote -->
                <div class="detail-card"
                  v-if="selectedReport.campanha && selectedReport.campanha.lista && selectedReport.campanha.lista.descricao">
                  <div class="detail-icon"
                    style="background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); color: white;">
                    <i class="fa-solid fa-file-lines"></i>
                  </div>
                  <div class="detail-content">
                    <span class="detail-label">Template</span>
                    <span class="detail-value">{{ selectedReport.campanha.lista.descricao }}</span>
                  </div>
                </div>

                <div class="detail-card"
                  v-if="selectedReport.campanha && selectedReport.campanha.lista && selectedReport.campanha.lista.arquivo_importado">
                  <div class="detail-icon"
                    style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white;">
                    <i class="fa-solid fa-box-archive"></i>
                  </div>
                  <div class="detail-content">
                    <span class="detail-label">Lote</span>
                    <span class="detail-value" style="font-family: monospace; font-size: 0.85rem;">
                      {{ formatLote(selectedReport.campanha.lista.arquivo_importado) }}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div class="info-section">
              <div class="section-header">
                <i class="fa-solid fa-chart-line"></i>
                <h4>Engajamento</h4>
              </div>
              <div class="metrics-grid">
                <div class="metric-card" :class="selectedReport.aberto ? 'metric-success' : 'metric-neutral'">
                  <div class="metric-icon"><i class="fa-solid fa-envelope-open"></i></div>
                  <div class="metric-content">
                    <span class="metric-label">Aberto</span>
                    <div class="metric-value-wrapper">
                      <span v-if="selectedReport.aberto" class="metric-value success">
                        <i class="fa-solid fa-check-circle"></i>
                        Sim ({{ selectedReport.total_aberturas }}x)
                      </span>
                      <span v-else class="metric-value neutral">
                        <i class="fa-solid fa-times-circle"></i>
                        Não
                      </span>
                    </div>
                    <div v-if="selectedReport.aberto" class="metric-details">
                      <div class="metric-detail-item">
                        <i class="fa-solid fa-clock"></i>
                        <span>Primeira: {{ formatDateTime(selectedReport.aberto_em) }}</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="metric-card" :class="selectedReport.total_cliques > 0 ? 'metric-info' : 'metric-neutral'">
                  <div class="metric-icon"><i class="fa-solid fa-mouse-pointer"></i></div>
                  <div class="metric-content">
                    <span class="metric-label">Cliques</span>
                    <div class="metric-value-wrapper">
                      <span class="metric-value" :class="selectedReport.total_cliques > 0 ? 'info' : 'neutral'">
                        <i
                          :class="selectedReport.total_cliques > 0 ? 'fa-solid fa-check-circle' : 'fa-solid fa-times-circle'"></i>
                        {{ selectedReport.total_cliques || 0 }} {{ selectedReport.total_cliques === 1 ? 'clique' :
                          'cliques'
                        }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedReport.clicado && clickDetails.length > 0" class="info-section">
              <div class="section-header">
                <i class="fa-solid fa-link"></i>
                <h4>Links Clicados</h4>
                <span class="badge-count">{{ clickDetails.length }}</span>
              </div>
              <div class="clicks-list-enhanced">
                <div v-for="(click, index) in clickDetails" :key="index" class="click-card">
                  <div class="click-card-header">
                    <div class="click-number">{{ index + 1 }}</div>
                    <div class="click-link">
                      <i class="fa-solid fa-link"></i>
                      <a :href="click.link" target="_blank" rel="noopener noreferrer" class="click-url-enhanced">{{
                        click.link }}</a>
                    </div>
                  </div>
                  <div class="click-card-body">
                    <div class="click-stat">
                      <i class="fa-solid fa-mouse-pointer"></i>
                      <span class="click-stat-value">{{ click.total_cliques }}</span>
                      <span class="click-stat-label">{{ click.total_cliques === 1 ? 'clique' : 'cliques' }}</span>
                    </div>
                    <div class="click-stat">
                      <i class="fa-solid fa-calendar-check"></i>
                      <span class="click-stat-value">{{ formatDateTime(click.primeiro_clique_em) }}</span>
                      <span class="click-stat-label">Primeiro</span>
                    </div>
                    <div v-if="click.ultimo_clique_em && click.primeiro_clique_em !== click.ultimo_clique_em"
                      class="click-stat">
                      <i class="fa-solid fa-calendar"></i>
                      <span class="click-stat-value">{{ formatDateTime(click.ultimo_clique_em) }}</span>
                      <span class="click-stat-label">Último</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedReport.erro" class="info-section error-section">
              <div class="section-header">
                <i class="fa-solid fa-triangle-exclamation"></i>
                <h4>Erro de Envio</h4>
              </div>
              <div class="error-content">
                <div class="error-icon-wrapper"><i class="fa-solid fa-circle-exclamation"></i></div>
                <p class="error-message">{{ selectedReport.erro }}</p>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeDetailsModal" class="btn btn-close-enhanced" type="button">
            <i class="fa-solid fa-xmark"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes — Contato Invalidado -->
    <div v-if="showInvalidadoModal" class="modal-overlay" @click="closeInvalidadoModal">
      <div class="modal-content-2 modal-large" @click.stop>
        <div class="modal-header">
          <div class="modal-header-content">
            <div class="modal-icon"><i class="fa-solid fa-circle-xmark"></i></div>
            <div class="modal-title-wrapper">
              <h3>Detalhes do Email Inválido</h3>
              <p class="modal-subtitle">Informações do contato e email invalidado</p>
            </div>
          </div>
          <button @click="closeInvalidadoModal" class="modal-close-btn" type="button">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <div class="modal-body" v-if="selectedInvalidado">
          <div class="info-section">
            <div class="section-header">
              <i class="fa-solid fa-user-circle"></i>
              <h4>Contato</h4>
            </div>
            <div class="detail-grid-enhanced">
              <div class="detail-card">
                <div class="detail-icon cpd-icon"><i class="fa-solid fa-id-card"></i></div>
                <div class="detail-content">
                  <span class="detail-label">CPD</span>
                  <span class="detail-value">{{ selectedInvalidado.cpd || '-' }}</span>
                </div>
              </div>
              <div class="detail-card">
                <div class="detail-icon cpf-icon"><i class="fa-solid fa-id-badge"></i></div>
                <div class="detail-content">
                  <span class="detail-label">CPF/CNPJ</span>
                  <span class="detail-value">{{ formatCpfCnpj(selectedInvalidado.cpf_cnpj) }}</span>
                </div>
              </div>
              <div class="detail-card full-width">
                <div class="detail-icon user-icon"><i class="fa-solid fa-user"></i></div>
                <div class="detail-content">
                  <span class="detail-label">Razão Social / Nome</span>
                  <span class="detail-value">{{ selectedInvalidado.nome || '-' }}</span>
                </div>
              </div>
            </div>
          </div>

          <div class="info-section error-section">
            <div class="section-header">
              <i class="fa-solid fa-envelope-circle-check"></i>
              <h4>Email Inválido</h4>
              <span class="status-badge"
                :class="selectedInvalidado.tipo === 'principal' ? 'status-sent' : 'status-delivered'"
                style="margin-left:10px">
                {{ selectedInvalidado.tipo === 'principal' ? 'Principal' : 'CC' }}
              </span>
            </div>
            <div class="detail-grid-enhanced">
              <div class="detail-card full-width">
                <div class="detail-icon email-icon"><i class="fa-solid fa-envelope"></i></div>
                <div class="detail-content">
                  <span class="detail-label">Endereço de Email</span>
                  <span class="detail-value" style="color:#ef4444;font-size:1.05rem">{{
                    selectedInvalidado.email_invalido
                    }}</span>
                </div>
              </div>
              <div class="detail-card">
                <div class="detail-icon calendar-icon"><i class="fa-solid fa-calendar-xmark"></i></div>
                <div class="detail-content">
                  <span class="detail-label">Validado em</span>
                  <span class="detail-value">{{ formatDateTime(selectedInvalidado.validado_em) }}</span>
                </div>
              </div>
              <div class="detail-card" v-if="selectedInvalidado.lista_template">
                <div class="detail-icon"
                  style="background: linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%); color: white;">
                  <i class="fa-solid fa-file-lines"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Template</span>
                  <span class="detail-value">{{ selectedInvalidado.lista_template }}</span>
                </div>
              </div>

              <div class="detail-card" v-if="selectedInvalidado.lista_arquivo">
                <div class="detail-icon"
                  style="background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%); color: white;">
                  <i class="fa-solid fa-box-archive"></i>
                </div>
                <div class="detail-content">
                  <span class="detail-label">Lote</span>
                  <span class="detail-value" style="font-family: monospace; font-size: 0.85rem;">
                    {{ formatLote(selectedInvalidado.lista_arquivo) }}
                  </span>
                </div>
              </div>
              <div class="detail-card full-width">
                <div class="detail-icon status-icon"><i class="fa-solid fa-triangle-exclamation"></i></div>
                <div class="detail-content">
                  <span class="detail-label">Motivo Completo</span>
                  <span class="detail-value">{{ selectedInvalidado.motivo_completo }}</span>
                </div>
              </div>
            </div>
          </div>

          <div
            v-if="selectedInvalidado.todos_invalidos && selectedInvalidado.todos_invalidos.filter(x => x.email !== selectedInvalidado.email_invalido).length > 0"
            class="info-section">
            <div class="section-header">
              <i class="fa-solid fa-list"></i>
              <h4>Outros emails inválidos deste contato</h4>
              <span class="badge-count">{{selectedInvalidado.todos_invalidos.filter(x => x.email !==
                selectedInvalidado.email_invalido).length}}</span>
            </div>
            <div class="clicks-list-enhanced">
              <div
                v-for="(inv, i) in selectedInvalidado.todos_invalidos.filter(x => x.email !== selectedInvalidado.email_invalido)"
                :key="i" class="click-card">
                <div class="click-card-header">
                  <div class="click-number">{{ i + 1 }}</div>
                  <div class="click-link">
                    <i class="fa-solid fa-envelope" style="color:#ef4444"></i>
                    <span style="font-weight:600;color:#ef4444">{{ inv.email }}</span>
                  </div>
                </div>
                <div class="click-card-body">
                  <div class="click-stat">
                    <i class="fa-solid fa-calendar"></i>
                    <span class="click-stat-value">{{ formatDateTime(inv.data_invalidacao) }}</span>
                    <span class="click-stat-label">validado</span>
                  </div>
                  <div class="click-stat">
                    <i class="fa-solid fa-triangle-exclamation"></i>
                    <span class="click-stat-value" style="font-size:0.8rem">{{ inv.motivo }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeInvalidadoModal" class="btn btn-close-enhanced" type="button">
            <i class="fa-solid fa-xmark"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'EmailReports',

  data() {
    return {
      sortKey: '',
      sortOrder: 'asc',
      exporting: false,
      loading: false,
      reports: [],
      searchQuery: '',
      searchType: 'cpd',
      searchTimeout: null,
      activeSearch: false,
      activeSearchQuery: '',
      currentPage: 1,
      itemsPerPage: 50,
      showDetailsModal: false,
      selectedReport: null,
      clickDetails: [],
      serverPagination: {
        total: 0, perPage: 10000, currentPage: 1, lastPage: 1, from: 0, to: 0
      },
      loadingMore: false,

      // ── Filtro local ──────────────────────────────────────
      localFilter: '',

      // ── Contatos Invalidados ──────────────────────────────
      invalidados: [],
      currentPageInv: 1,
      invalidadosPagination: {
        total: 0, perPage: 10000, currentPage: 1, lastPage: 1,
      },
      showInvalidadoModal: false,
      selectedInvalidado: null,
    };
  },

  computed: {
    // ── modo ativo ──────────────────────────────────────────
    isInvalidadosMode() {
      return this.activeSearch && this.searchType === 'contatos_invalidados';
    },

    currentTableEmpty() {
      if (this.isInvalidadosMode) return this.filteredInvalidados.length === 0;
      return this.filteredReports.length === 0;
    },

    // Contador unificado para o badge do filtro local
    currentFilteredCount() {
      return this.isInvalidadosMode ? this.filteredInvalidados.length : this.filteredReports.length;
    },

    // ── envios normais (sort + filtro local) ────────────────
    filteredReports() {
      let list = this.reports;

      // Filtro local
      const q = this.localFilter.trim().toLowerCase();
      if (q) {
        list = list.filter(r =>
          (r.email || '').toLowerCase().includes(q) ||
          (this.getContactName(r) || '').toLowerCase().includes(q) ||
          (this.getContactCpd(r) || '').toLowerCase().includes(q) ||
          (this.getContactCpfCnpj(r) || '').toLowerCase().includes(q) ||
          (this.getCampaignName(r) || '').toLowerCase().includes(q)
        );
      }

      if (!this.sortKey) return list;

      return [...list].sort((a, b) => {
        let valA, valB;
        if (this.sortKey === 'cpd') { valA = this.getContactCpd(a) || ''; valB = this.getContactCpd(b) || ''; }
        else if (this.sortKey === 'cpf_cnpj') { valA = this.getContactCpfCnpj(a) || ''; valB = this.getContactCpfCnpj(b) || ''; }
        else if (this.sortKey === 'nome') { valA = this.getContactName(a) || ''; valB = this.getContactName(b) || ''; }
        else if (this.sortKey === 'campanha') { valA = this.getCampaignName(a) || ''; valB = this.getCampaignName(b) || ''; }
        else if (this.sortKey === 'aberto') { valA = a.aberto ? 1 : 0; valB = b.aberto ? 1 : 0; }
        else if (this.sortKey === 'total_cliques') { valA = a.total_cliques || 0; valB = b.total_cliques || 0; }
        else if (this.sortKey === 'enviado_em' || this.sortKey === 'aberto_em') {
          valA = a[this.sortKey] ? new Date(a[this.sortKey]).getTime() : 0;
          valB = b[this.sortKey] ? new Date(b[this.sortKey]).getTime() : 0;
        } else { valA = a[this.sortKey] || ''; valB = b[this.sortKey] || ''; }
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();
        if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },

    paginatedReports() {
      return this.filteredReports.slice(this.startIndex, this.endIndex);
    },

    totalPages() { return Math.ceil(this.filteredReports.length / this.itemsPerPage); },
    startIndex() { return (this.currentPage - 1) * this.itemsPerPage; },
    endIndex() {
      const end = this.startIndex + this.itemsPerPage;
      return end > this.filteredReports.length ? this.filteredReports.length : end;
    },
    visiblePages() { return this._buildVisiblePages(this.currentPage, this.totalPages); },
    hasMoreData() { return this.serverPagination.currentPage < this.serverPagination.lastPage; },
    totalServerRecords() { return this.serverPagination.total; },
    loadedPercentage() {
      if (this.totalServerRecords === 0) return 0;
      return Math.round((this.reports.length / this.totalServerRecords) * 100);
    },

    // ── invalidados (sort + filtro local) ──────────────────
    filteredInvalidados() {
      let list = this.invalidados;

      const q = this.localFilter.trim().toLowerCase();
      if (q) {
        list = list.filter(r =>
          (r.email_invalido || '').toLowerCase().includes(q) ||
          (r.nome || '').toLowerCase().includes(q) ||
          (r.cpd || '').toLowerCase().includes(q) ||
          (r.cpf_cnpj || '').toLowerCase().includes(q)
        );
      }

      if (!this.sortKey) return list;

      return [...list].sort((a, b) => {
        let valA = a[this.sortKey] || '';
        let valB = b[this.sortKey] || '';
        if (this.sortKey === 'validado_em') {
          valA = valA ? new Date(valA).getTime() : 0;
          valB = valB ? new Date(valB).getTime() : 0;
        } else {
          if (typeof valA === 'string') valA = valA.toLowerCase();
          if (typeof valB === 'string') valB = valB.toLowerCase();
        }
        if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
        if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    },

    paginatedInvalidados() { return this.filteredInvalidados.slice(this.startIndexInv, this.endIndexInv); },
    totalPagesInv() { return Math.ceil(this.filteredInvalidados.length / this.itemsPerPage); },
    startIndexInv() { return (this.currentPageInv - 1) * this.itemsPerPage; },
    endIndexInv() {
      const end = this.startIndexInv + this.itemsPerPage;
      return end > this.filteredInvalidados.length ? this.filteredInvalidados.length : end;
    },
    visiblePagesInv() { return this._buildVisiblePages(this.currentPageInv, this.totalPagesInv); },
    hasMoreInvalidadosData() { return this.invalidadosPagination.currentPage < this.invalidadosPagination.lastPage; },
    totalInvalidadosRecords() { return this.invalidadosPagination.total; },
    loadedInvalidadosPercentage() {
      if (this.totalInvalidadosRecords === 0) return 0;
      return Math.round((this.invalidados.length / this.totalInvalidadosRecords) * 100);
    },
  },

  watch: {
    showDetailsModal(newVal) { document.body.style.overflow = newVal ? 'hidden' : ''; },
    showInvalidadoModal(newVal) { document.body.style.overflow = newVal ? 'hidden' : ''; },
    // Volta pra página 1 ao filtrar localmente
    localFilter() { this.currentPage = 1; this.currentPageInv = 1; }
  },

  mounted() { },

  beforeDestroy() { document.body.style.overflow = ''; },

  methods: {
    formatLote(arquivo) {
      if (!arquivo) return '-';
      const match = arquivo.match(/_old\/(.+)$/);
      return match ? match[1] : arquivo;
    },
    // ── highlight do filtro local ───────────────────────────
    highlight(text) {
      if (!this.localFilter.trim()) return text;
      const q = this.localFilter.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return (text || '').replace(new RegExp(`(${q})`, 'gi'), '<mark class="hl">$1</mark>');
    },

    // ── helpers ─────────────────────────────────────────────
    _buildVisiblePages(current, total) {
      const pages = [];
      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);
      if (end - start < 4) {
        if (start === 1) end = Math.min(total, start + 4);
        else if (end === total) start = Math.max(1, end - 4);
      }
      for (let i = start; i <= end; i++) pages.push(i);
      return pages;
    },

    onSearchTypeChange() { this.searchQuery = ''; },

    sortBy(key) {
      if (this.sortKey === key) { this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'; }
      else { this.sortKey = key; this.sortOrder = 'asc'; }
      this.currentPage = 1; this.currentPageInv = 1;
    },

    getSortIcon(key) {
      if (this.sortKey !== key) return 'fa-solid fa-sort sort-icon-inactive';
      return this.sortOrder === 'asc' ? 'fa-solid fa-sort-up sort-icon-active' : 'fa-solid fa-sort-down sort-icon-active';
    },

    // ── exportar ─────────────────────────────────────────────
    async exportToExcel() {
      const source = this.isInvalidadosMode ? this.filteredInvalidados : this.filteredReports;
      if (source.length === 0) {
        Swal.fire({ type: 'warning', title: 'Nenhum registro', text: 'Não há registros para exportar', confirmButtonColor: '#667eea' });
        return;
      }
      Swal.fire({
        type: 'question', title: 'Confirmar exportação',
        text: `Deseja exportar ${this.formatNumber(source.length)} registro(s) para Excel?`,
        showCancelButton: true, confirmButtonText: 'Sim, exportar', cancelButtonText: 'Cancelar',
        confirmButtonColor: '#10b981', cancelButtonColor: '#6c757d'
      }).then((result) => {
        if (result.value === true || result.isConfirmed === true) { this.executeExport(); }
      });
    },

    async executeExport() {
      this.exporting = true;
      Swal.fire({ title: 'Exportando...', text: 'Aguarde enquanto geramos o arquivo', allowOutsideClick: false, allowEscapeKey: false, onOpen: () => { Swal.showLoading(); } });
      try {
        let dados, fileName;

        if (this.isInvalidadosMode) {
          // ── Exporta contatos invalidados ──────────────────────────────
          dados = this.filteredInvalidados.map(row => [
            row.cpd || '-',
            this.formatCpfCnpj(row.cpf_cnpj),
            row.nome || '-',
            row.email_invalido || '-',
            row.tipo === 'principal' ? 'Principal' : 'CC',
            'Inválido',
            this.formatDateTime(row.validado_em),
            row.lista_template || '-',
            this.formatLote(row.lista_arquivo),
            row.motivo_completo || row.motivo_curto || '-'
          ]);
          fileName = `emails_invalidos_${new Date().getTime()}.xlsx`;
        } else {
          // ── Exporta envios normais ────────────────────────────────────
          dados = this.filteredReports.map(report => [
            report.email || '-',
            this.getContactName(report),
            this.getContactCpd(report),
            this.formatCpfCnpj(this.getContactCpfCnpj(report)),
            this.getCampaignName(report),
            // 🆕
            (report.campanha && report.campanha.lista ? report.campanha.lista.descricao || '-' : '-'),
            (report.campanha && report.campanha.lista ? this.formatLote(report.campanha.lista.arquivo_importado) : '-'),
            this.getStatusLabel(report.status),
            this.formatDateTime(report.enviado_em),
            report.aberto ? 'Sim' : 'Não',
            report.total_aberturas || 0,
            this.formatDateTime(report.aberto_em),
            report.clicado ? 'Sim' : 'Não',
            report.total_cliques || 0,
            report.brevo_message_id || '-',
            report.erro || '-'
          ]);
          fileName = `relatorio_envios_${new Date().getTime()}.xlsx`;
        }

        const total = dados.length;

        const response = await fetch('/emails/api/sends/export', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content
          },
          body: JSON.stringify({
            dados,
            tipo: this.isInvalidadosMode ? 'invalidados' : 'envios'
          })
        });
        if (response.ok) {
          const blob = await response.blob();
          Swal.close();
          await new Promise(resolve => setTimeout(resolve, 100));
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url; a.download = fileName;
          document.body.appendChild(a); a.click();
          setTimeout(() => { window.URL.revokeObjectURL(url); document.body.removeChild(a); }, 100);
          setTimeout(() => { Swal.fire({ type: 'success', title: 'Exportação concluída!', text: `${this.formatNumber(total)} registro(s) exportado(s) com sucesso!`, confirmButtonColor: '#10b981', timer: 3000 }); }, 200);
        } else {
          Swal.close();
          const contentType = response.headers.get('content-type');
          if (contentType && contentType.includes('application/json')) {
            const error = await response.json();
            Swal.fire({ type: 'error', title: 'Erro ao exportar', text: error.message || 'Erro desconhecido', confirmButtonColor: '#667eea' });
          } else {
            Swal.fire({ type: 'error', title: 'Erro ao exportar', text: 'Ocorreu um erro inesperado.', confirmButtonColor: '#667eea' });
          }
        }
      } catch (error) {
        Swal.close();
        Swal.fire({ type: 'error', title: 'Erro ao exportar', text: error.message || 'Ocorreu um erro inesperado.', confirmButtonColor: '#667eea' });
      } finally { this.exporting = false; }
    },

    // ── pesquisa ─────────────────────────────────────────────
    handleSearchInput() {
      if (this.searchType === 'cpd') { this.searchQuery = this.searchQuery.replace(/\D/g, ''); }
    },

    async performSearch(loadMore = false) {
      if (this.searchType !== 'contatos_invalidados' && !this.searchQuery.trim()) return;
      if (this.searchType === 'contatos_invalidados') { await this.fetchInvalidados(loadMore); return; }

      if (loadMore) { this.loadingMore = true; }
      else { this.loading = true; this.reports = []; this.localFilter = ''; this.serverPagination.currentPage = 1; }

      try {
        const params = new URLSearchParams();
        params.append('searchType', this.searchType);
        params.append('searchQuery', this.searchQuery.trim());
        params.append('page', loadMore ? this.serverPagination.currentPage + 1 : 1);
        params.append('per_page', 10000);
        const response = await fetch(`/emails/api/sends/search?${params.toString()}`, {
          headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content }
        });
        if (response.ok) {
          const result = await response.json();
          if (loadMore) { this.reports = [...this.reports, ...(result.data || [])]; }
          else { this.reports = result.data || []; this.activeSearch = true; this.activeSearchQuery = this.searchQuery.trim(); this.currentPage = 1; }
          this.serverPagination = { total: result.pagination?.total || 0, perPage: result.pagination?.per_page || 10000, currentPage: result.pagination?.current_page || 1, lastPage: result.pagination?.last_page || 1, from: result.pagination?.from || 0, to: result.pagination?.to || 0 };
        } else {
          const error = await response.json();
          alert(error.message || 'Erro ao buscar envios');
        }
      } catch (error) {
        console.error('Erro ao buscar envios:', error);
        alert('Erro ao buscar envios. Tente novamente.');
      } finally { this.loading = false; this.loadingMore = false; }
    },

    // ── invalidados ──────────────────────────────────────────
    async fetchInvalidados(loadMore = false) {
      if (loadMore) { this.loadingMore = true; }
      else { this.loading = true; this.invalidados = []; this.localFilter = ''; this.invalidadosPagination.currentPage = 1; }
      try {
        const page = loadMore ? this.invalidadosPagination.currentPage + 1 : 1;
        const params = new URLSearchParams({ page, per_page: 10000 });
        const response = await fetch(`/emails/api/contatos-invalidos/invalidados?${params}`, {
          headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content }
        });
        if (response.ok) {
          const result = await response.json();
          const flat = result.data || [];
          if (loadMore) { this.invalidados = [...this.invalidados, ...flat]; }
          else { this.invalidados = flat; this.activeSearch = true; this.activeSearchQuery = ''; this.currentPageInv = 1; }
          this.invalidadosPagination = { total: result.pagination?.total || 0, perPage: result.pagination?.per_page || 10000, currentPage: result.pagination?.current_page || page, lastPage: result.pagination?.last_page || 1 };
        } else {
          const error = await response.json().catch(() => ({}));
          alert(error.message || 'Erro ao buscar contatos invalidados');
        }
      } catch (error) {
        console.error('Erro ao buscar invalidados:', error);
        alert('Erro ao buscar contatos invalidados. Tente novamente.');
      } finally { this.loading = false; this.loadingMore = false; }
    },

    async loadMoreInvalidados() {
      if (!this.hasMoreInvalidadosData || this.loadingMore) return;
      const scrollY = window.scrollY; await this.fetchInvalidados(true); await this.$nextTick(); window.scrollTo(0, scrollY);
    },

    viewInvalidadoDetails(row) { this.selectedInvalidado = row; this.showInvalidadoModal = true; },
    closeInvalidadoModal() { this.showInvalidadoModal = false; setTimeout(() => { this.selectedInvalidado = null; }, 300); },

    async loadMoreData() {
      if (!this.hasMoreData || this.loadingMore) return;
      const currentScrollY = window.scrollY; await this.performSearch(true); await this.$nextTick(); window.scrollTo(0, currentScrollY);
    },

    clearSearch() { this.searchQuery = ''; },

    clearAllFilters() {
      this.searchQuery = ''; this.localFilter = ''; this.activeSearch = false; this.activeSearchQuery = '';
      this.reports = []; this.invalidados = []; this.currentPage = 1; this.currentPageInv = 1;
      this.serverPagination = { total: 0, perPage: 10000, currentPage: 1, lastPage: 1, from: 0, to: 0 };
      this.invalidadosPagination = { total: 0, perPage: 10000, currentPage: 1, lastPage: 1 };
    },

    getSearchPlaceholder() {
      const placeholders = { cpd: 'Digite o CPD exato (apenas números)...', email: 'Digite parte do email (ex: usuario ou @gmail.com)...', nome: 'Digite parte do nome...', cpf_cnpj: 'Digite o CPF ou CNPJ completo...', contatos_invalidados: '' };
      return placeholders[this.searchType] || 'Digite sua pesquisa...';
    },

    getSearchTypeLabel() {
      const labels = { cpd: 'CPD', email: 'Email', nome: 'Nome', cpf_cnpj: 'CPF/CNPJ', contatos_invalidados: 'Contatos Invalidados' };
      return labels[this.searchType] || this.searchType;
    },

    async viewDetails(report) {
      this.selectedReport = report; this.clickDetails = []; this.showDetailsModal = true;
      if (report.clicado && report.total_cliques > 0) { await this.loadClickDetails(report.id); }
    },

    async loadClickDetails(envioId) {
      try {
        const response = await fetch(`/emails/api/sends/${envioId}/clicks`, { headers: { 'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').content } });
        if (response.ok) { const result = await response.json(); this.clickDetails = result.data || []; }
      } catch (error) { console.error('Erro ao carregar detalhes de cliques:', error); this.clickDetails = []; }
    },

    closeDetailsModal() { this.showDetailsModal = false; setTimeout(() => { this.selectedReport = null; this.clickDetails = []; }, 300); },

    getContactName(report) {
      if (!report.contato) return '-';
      const nome = report.contato.nome || ''; const sobrenome = report.contato.sobrenome || '';
      return `${nome} ${sobrenome}`.trim() || report.contato.nome_fantasia || '-';
    },
    getContactCpd(report) { return report.contato?.cpd || '-'; },
    getContactCpfCnpj(report) { return report.contato?.cpf_cnpj || null; },
    getCampaignName(report) { return report.campanha?.nome || 'Campanha não identificada'; },

    nextPage() { if (this.currentPage < this.totalPages) { this.currentPage++; this.scrollToTop(); } },
    previousPage() { if (this.currentPage > 1) { this.currentPage--; this.scrollToTop(); } },
    goToPage(page) { if (page >= 1 && page <= this.totalPages) { this.currentPage = page; this.scrollToTop(); } },
    nextPageInv() { if (this.currentPageInv < this.totalPagesInv) { this.currentPageInv++; this.scrollToTop(); } },
    previousPageInv() { if (this.currentPageInv > 1) { this.currentPageInv--; this.scrollToTop(); } },
    goToPageInv(page) { if (page >= 1 && page <= this.totalPagesInv) { this.currentPageInv = page; this.scrollToTop(); } },
    scrollToTop() { window.scrollTo({ top: 0, behavior: 'smooth' }); },

    getStatusClass(status) {
      const classes = { pendente: 'status-pending', enviado: 'status-sent', entregue: 'status-delivered', aberto: 'status-opened', clicado: 'status-clicked', bounce: 'status-bounced', erro: 'status-error' };
      return classes[status] || 'status-default';
    },
    getStatusIcon(status) {
      const icons = { pendente: 'fa-solid fa-clock', enviado: 'fa-solid fa-paper-plane', entregue: 'fa-solid fa-check', aberto: 'fa-solid fa-envelope-open', clicado: 'fa-solid fa-mouse-pointer', bounce: 'fa-solid fa-circle-exclamation', erro: 'fa-solid fa-triangle-exclamation' };
      return icons[status] || 'fa-solid fa-circle';
    },
    getStatusLabel(status) {
      const labels = { pendente: 'Pendente', enviado: 'Enviado', entregue: 'Entregue', aberto: 'Aberto', clicado: 'Clicado', bounce: 'Bounce', erro: 'Erro' };
      return labels[status] || status;
    },

    formatNumber(num) { return new Intl.NumberFormat('pt-BR').format(num || 0); },
    formatDateTime(dateString) {
      if (!dateString) return '-';
      return new Date(dateString).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    formatCpfCnpj(value) {
      if (!value) return '-';
      const numbers = value.replace(/\D/g, '');
      if (numbers.length === 11) return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
      if (numbers.length === 14) return numbers.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
      return value;
    }
  }
};
</script>

<style scoped>
.email-reports {
  width: 100%;
  min-height: 100vh;
  background: #ffffff;
  padding: 40px 20px;
}

.reports-container {
  max-width: 1600px;
  margin: 0 auto;
}

.reports-table-section {
  background: white;
  border-radius: 16px !important;
  padding: 30px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
  border: 1px solid #e0e0e0;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
  flex-wrap: wrap;
  gap: 15px;
}

.table-header h2 {
  margin: 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1.8rem;
}

.table-info {
  color: #666;
  font-size: 14px;
  font-weight: 600;
}

.search-bar {
  margin-bottom: 25px;
  padding-bottom: 25px;
  border-bottom: 2px solid #e0e0e0;
}

.search-controls {
  display: grid;
  grid-template-columns: 200px 1fr auto;
  gap: 15px;
  align-items: end;
}

.search-type-selector {
  display: flex;
  flex-direction: column;
}

.search-type-selector label {
  display: block;
  font-weight: 600;
  color: #555;
  margin-bottom: 8px;
  font-size: 14px;
}

.search-type-select {
  width: 100%;
  padding: 12px;
  border: 2px solid #e0e0e0;
  border-radius: 8px !important;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s;
  background: white;
  cursor: pointer;
  height: 46px;
}

.search-type-select:focus {
  outline: none;
  border-color: #667eea;
}

.invalidados-info-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 16px;
  height: 46px;
  background: #fef2f2;
  border: 2px solid #fca5a5;
  border-radius: 8px !important;
  color: #991b1b;
  font-size: 13.5px;
}

.invalidados-info-box i {
  color: #ef4444;
  font-size: 16px;
  flex-shrink: 0;
}

.search-input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.search-icon {
  position: absolute;
  left: 15px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
  font-size: 16px;
  pointer-events: none;
  z-index: 1;
}

.search-input {
  width: 100%;
  padding: 12px 45px 12px 45px;
  border: 2px solid #e0e0e0;
  border-radius: 8px !important;
  font-size: 14px;
  transition: all 0.3s;
  height: 46px;
}

.search-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.clear-search-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: #e0e0e0;
  border: none;
  width: 28px;
  height: 28px;
  border-radius: 50% !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
  z-index: 2;
}

.clear-search-btn:hover {
  background: #667eea;
  color: white;
}

.btn-search {
  padding: 12px 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px !important;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  transition: all 0.3s;
  white-space: nowrap;
  height: 46px;
  align-self: end;
}

.btn-search:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(102, 126, 234, 0.4);
}

.btn-search:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.active-search-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 15px;
  padding: 12px 15px;
  background: #e3f2fd;
  border-radius: 8px !important;
  border-left: 4px solid #1976d2;
}

.search-badge {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #1565c0;
  font-size: 14px;
}

.search-badge i {
  font-size: 16px;
}

.btn-clear-filters {
  padding: 6px 15px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 6px !important;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
}

.btn-clear-filters:hover {
  background: #d32f2f;
  transform: translateY(-1px);
}

/* ─── FILTRO LOCAL ───────────────────────────────────── */
.local-filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding: 12px 16px;
  background: #f0f4ff;
  border-radius: 10px !important;
  border: 1px solid #c7d2fe;
}

.local-filter-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #4338ca;
  white-space: nowrap;
  flex-shrink: 0;
}

.local-filter-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}

.local-filter-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #667eea;
  font-size: 15px;
  pointer-events: none;
  z-index: 1;
}

.local-filter-input {
  width: 100%;
  padding: 9px 40px 9px 42px;
  border: 2px solid #c7d2fe;
  border-radius: 8px !important;
  font-size: 13.5px;
  transition: all 0.3s;
  background: white;
  height: 40px;
}

.local-filter-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.15);
}

.clear-local-btn {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: #e0e0e0;
  border: none;
  width: 26px;
  height: 26px;
  border-radius: 50% !important;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s;
  color: #666;
  z-index: 2;
  font-size: 12px;
}

.clear-local-btn:hover {
  background: #667eea;
  color: white;
}

.local-filter-count {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  padding: 5px 12px;
  background: white;
  border-radius: 6px !important;
  border: 1px solid #c7d2fe;
  flex-shrink: 0;
}

.local-filter-count i {
  color: #10b981;
}

.local-filter-count.count-empty {
  color: #ef4444;
}

.local-filter-count.count-empty i {
  color: #ef4444;
}

/* Highlight do filtro local */
:deep(.hl) {
  background: #fff176;
  color: #212529;
  border-radius: 2px;
  padding: 0 1px;
  font-weight: 700;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #667eea;
  border-radius: 50% !important;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

.table-responsive {
  overflow-x: auto;
}

.reports-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 1400px;
}

.reports-table thead {
  background: #f8f9fa;
}

.reports-table th,
.reports-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
  vertical-align: middle;
}

.reports-table th {
  font-weight: 600;
  color: #555;
  white-space: nowrap;
}

.email-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #667eea;
  font-weight: 500;
  white-space: nowrap;
}


.email-cell i {
  flex-shrink: 0;
}

.email-cell span {
  word-break: break-word;
}

.campaign-name {
  font-weight: 600;
  color: #764ba2;
}

.text-center {
  text-align: center !important;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 12px !important;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-pending {
  background: #fff3e0;
  color: #f57c00;
}

.status-sent {
  background: #e3f2fd;
  color: #1976d2;
}

.status-delivered {
  background: #f3e5f5;
  color: #7b1fa2;
}

.status-opened {
  background: #e8f5e9;
  color: #388e3c;
}

.status-clicked {
  background: #e1f5fe;
  color: #0288d1;
}

.status-bounced {
  background: #ffebee;
  color: #c62828;
}

.status-error {
  background: #fce4ec;
  color: #ad1457;
}

.badge-opened,
.badge-not-opened {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px !important;
  font-size: 12px;
  font-weight: 600;
}

.badge-opened {
  background: #e8f5e9;
  color: #388e3c;
}

.badge-not-opened {
  background: #ffebee;
  color: #c62828;
}

.badge-clicks,
.badge-no-clicks {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px !important;
  font-size: 12px;
  font-weight: 600;
}

.badge-clicks {
  background: #fff3e0;
  color: #f57c00;
}

.badge-no-clicks {
  color: #999;
}

.actions-cell {
  text-align: center;
}

.btn-action {
  padding: 6px 12px;
  border: none;
  border-radius: 6px !important;
  cursor: pointer;
  transition: all 0.3s;
  font-size: 14px;
}

.btn-view {
  background: #667eea;
  color: white;
}

.btn-view:hover {
  background: #5568d3;
  transform: translateY(-2px);
}

.empty-state {
  text-align: center;
  padding: 80px 20px;
  color: #999;
}

.empty-icon {
  font-size: 80px;
  margin-bottom: 20px;
  opacity: 0.3;
}

.empty-state h3 {
  margin: 0 0 10px 0;
  color: #666;
  font-size: 1.5rem;
}

.empty-state p {
  color: #999;
  font-size: 14px;
}

.load-more-container {
  margin-top: 25px;
  display: flex;
  justify-content: center;
}

.load-more-content {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px !important;
  padding: 15px 20px;
  display: flex;
  align-items: center;
  gap: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.3s;
}

.load-more-content:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: #667eea;
}

.load-more-stats {
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-size: 14px;
}

.stats-loaded {
  font-weight: 700;
  color: #667eea;
  font-size: 18px;
}

.stats-separator {
  color: #d1d5db;
  font-weight: 300;
}

.stats-total {
  font-weight: 600;
  color: #6c757d;
  font-size: 16px;
}

.stats-label {
  color: #9ca3af;
  font-size: 13px;
  margin-left: 2px;
}

.progress-mini {
  width: 100px;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px !important;
  overflow: hidden;
  position: relative;
}

.progress-mini-fill {
  height: 100%;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 100%);
  border-radius: 3px !important;
  transition: width 0.5s ease;
  position: relative;
}

.progress-mini-fill::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  animation: shimmer-mini 2s infinite;
}

@keyframes shimmer-mini {
  0% {
    transform: translateX(-100%);
  }

  100% {
    transform: translateX(100%);
  }
}

.btn-load-compact {
  padding: 8px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px !important;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
  white-space: nowrap;
}

.btn-load-compact:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-load-compact:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-load-compact i {
  font-size: 12px;
}

.text-muted {
  color: #6c757d;
  font-size: 0.9em;
}

.pagination-container {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #e0e0e0;
}

.pagination-info {
  text-align: center;
  color: #666;
  font-size: 14px;
  margin-bottom: 15px;
}

.pagination-controls {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.pagination-pages {
  display: flex;
  gap: 4px;
}

.btn {
  padding: 8px 12px;
  border: none;
  border-radius: 6px !important;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  min-width: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  overflow-y: auto;
  padding: 20px;
}

.modal-content-2 {
  background: white;
  border-radius: 12px !important;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  margin: auto;
}

.modal-large {
  max-width: 1100px;
}

.modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 30px;
  border-radius: 12px 12px 0 0 !important;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.modal-header-content {
  display: flex;
  align-items: center;
  gap: 15px;
  flex: 1;
}

.modal-icon {
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  flex-shrink: 0;
}

.modal-title-wrapper h3 {
  color: white;
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.modal-subtitle {
  margin: 5px 0 0 0;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 400;
}

.modal-close-btn {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 8px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  border: none;
  cursor: pointer;
  transition: all 0.3s;
  flex-shrink: 0;
}

.modal-close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: rotate(90deg);
}

.modal-body {
  padding: 25px;
  overflow-y: auto;
  flex: 1;
}

.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px !important;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px !important;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.info-section {
  margin-bottom: 30px;
}

.info-section:last-child {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f0f0;
}

.section-header i {
  font-size: 20px;
  color: #667eea;
}

.section-header h4 {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.badge-count {
  margin-left: auto;
  background: #667eea;
  color: white;
  padding: 4px 12px;
  border-radius: 12px !important;
  font-size: 0.75rem;
  font-weight: 600;
}

.detail-grid-enhanced {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 15px;
}

.detail-card {
  background: #f8f9fa;
  border-radius: 12px !important;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 15px;
  border: 1px solid #e9ecef;
  transition: all 0.3s;
}

.detail-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border-color: #667eea;
}

.detail-card.full-width {
  grid-column: 1 / -1;
}

.detail-icon {
  width: 45px;
  height: 45px;
  border-radius: 10px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.email-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.user-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
  color: white;
}

.cpd-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
  color: white;
}

.cpf-icon {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
  color: white;
}

.campaign-icon {
  background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
  color: white;
}

.message-icon {
  background: linear-gradient(135deg, #a8edea 0%, #fed6e3 100%);
  color: #333;
}

.status-icon {
  background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
  color: #333;
}

.calendar-icon {
  background: linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%);
  color: white;
}

.detail-content {
  flex: 1;
  min-width: 0;
}

.detail-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 5px;
}

.detail-value {
  display: block;
  font-size: 0.95rem;
  font-weight: 600;
  color: #212529;
  word-break: break-word;
}

.detail-value.small-text {
  font-size: 0.8rem;
  font-family: monospace;
}

.detail-value.campaign-name {
  color: #667eea;
  font-size: 1rem;
}

.status-badge-enhanced {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px !important;
  font-size: 0.85rem;
  font-weight: 600;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 15px;
}

.metric-card {
  background: #f8f9fa;
  border-radius: 12px !important;
  padding: 20px;
  border: 2px solid #e9ecef;
  transition: all 0.3s;
}

.metric-card.metric-success {
  border-color: #10b981;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
}

.metric-card.metric-info {
  border-color: #3b82f6;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
}

.metric-card.metric-neutral {
  border-color: #d1d5db;
}

.metric-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
}

.metric-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px !important;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin-bottom: 15px;
  color: #667eea;
}

.metric-content {
  flex: 1;
}

.metric-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #6c757d;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.metric-value-wrapper {
  margin-bottom: 10px;
}

.metric-value {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 1.2rem;
  font-weight: 700;
  padding: 8px 16px;
  border-radius: 8px !important;
}

.metric-value.success {
  color: #059669;
  background: white;
}

.metric-value.info {
  color: #2563eb;
  background: white;
}

.metric-value.neutral {
  color: #6c757d;
  background: white;
}

.metric-details {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.metric-detail-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  color: #495057;
  margin-bottom: 5px;
}

.metric-detail-item:last-child {
  margin-bottom: 0;
}

.metric-detail-item i {
  color: #667eea;
}

.clicks-list-enhanced {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.click-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 12px !important;
  border: 1px solid #dee2e6;
  overflow: hidden;
  transition: all 0.3s;
}

.click-card:hover {
  transform: translateX(5px);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.2);
  border-color: #667eea;
}

.click-card-header {
  background: white;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  border-bottom: 1px solid #dee2e6;
}

.click-number {
  width: 35px;
  height: 35px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border-radius: 8px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.95rem;
  flex-shrink: 0;
}

.click-link {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.click-link i {
  color: #667eea;
  font-size: 16px;
}

.click-url-enhanced {
  color: #667eea;
  text-decoration: none;
  font-weight: 500;
  font-size: 0.9rem;
  word-break: break-all;
  transition: all 0.2s;
}

.click-url-enhanced:hover {
  color: #764ba2;
  text-decoration: underline;
}

.click-card-body {
  padding: 15px;
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.click-stat {
  display: flex;
  align-items: center;
  gap: 8px;
}

.click-stat i {
  color: #6c757d;
  font-size: 14px;
}

.click-stat-value {
  font-weight: 600;
  color: #212529;
  font-size: 0.9rem;
}

.click-stat-label {
  font-size: 0.75rem;
  color: #6c757d;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.error-section {
  background: linear-gradient(135deg, #fef3f2 0%, #fee2e2 100%);
  border-radius: 12px !important;
  border: 2px solid #fca5a5;
  padding: 20px;
}

.error-section .section-header {
  border-bottom-color: #fca5a5;
}

.error-section .section-header i {
  color: #ef4444;
}

.error-content {
  display: flex;
  gap: 15px;
  align-items: flex-start;
}

.error-icon-wrapper {
  width: 50px;
  height: 50px;
  background: white;
  border-radius: 12px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-size: 24px;
  color: #ef4444;
}

.error-message {
  flex: 1;
  margin: 0;
  padding: 12px;
  background: white;
  border-radius: 8px !important;
  color: #991b1b;
  font-weight: 500;
  line-height: 1.6;
}

.modal-footer {
  background: #f8f9fa;
  padding: 20px 30px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  flex-shrink: 0;
}

.btn-close-enhanced {
  padding: 12px 24px;
  background: linear-gradient(135deg, #6c757d 0%, #495057 100%);
  color: white;
  border: none;
  border-radius: 8px !important;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;
}

.btn-close-enhanced:hover {
  background: linear-gradient(135deg, #495057 0%, #343a40 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

@media (max-width: 1024px) {
  .search-controls {
    grid-template-columns: 1fr;
  }

  .btn-search {
    width: 100%;
  }
}

@media (max-width: 768px) {
  .load-more-content {
    flex-direction: column;
    gap: 12px;
    padding: 15px;
  }

  .progress-mini {
    width: 100%;
  }

  .btn-load-compact {
    width: 100%;
    justify-content: center;
  }

  .modal-header-content {
    gap: 10px;
  }

  .modal-icon {
    width: 40px;
    height: 40px;
    font-size: 20px;
  }

  .modal-title-wrapper h3 {
    font-size: 1.2rem;
  }

  .detail-grid-enhanced,
  .metrics-grid {
    grid-template-columns: 1fr;
  }

  .click-card-body {
    flex-direction: column;
    gap: 10px;
  }

  .active-search-info {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }

  .local-filter-bar {
    flex-direction: column;
    align-items: stretch;
  }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 15px;
}

.btn-export {
  padding: 10px 20px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  border-radius: 8px !important;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
  white-space: nowrap;
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.btn-export:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
}

.btn-export:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-export i {
  font-size: 16px;
}

@media (max-width: 768px) {
  .header-actions {
    flex-direction: column;
    width: 100%;
    align-items: stretch;
  }

  .btn-export {
    width: 100%;
    justify-content: center;
  }
}

.sortable-th {
  cursor: pointer;
  user-select: none;
  white-space: nowrap;
  transition: background 0.2s;
}

.sortable-th:hover {
  background: #eef0f8;
}

.sort-icons {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  font-size: 12px;
  vertical-align: middle;
}

.sort-icon-inactive {
  color: #ccc;
}

.sort-icon-active {
  color: #667eea;
}

.info-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 11px 18px;
  border-radius: 8px !important;
  font-size: 13.5px;
  margin-bottom: 16px;
  border-left: 4px solid;
}

.banner-brevo {
  background: #fff7ed;
  border-color: #f97316;
  color: #7c2d12;
}

.banner-brevo i {
  color: #f97316;
  font-size: 16px;
  flex-shrink: 0;
}

.banner-api {
  background: #f0fdf4;
  border-color: #22c55e;
  color: #14532d;
}

.banner-api i {
  color: #22c55e;
  font-size: 16px;
  flex-shrink: 0;
}
</style>